import { Initiative } from '@schema/initiative'
import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../blocks/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { useEffect, useState } from 'react'
import { useInitiativeAutoSave } from '../blocks/useInitiativeAutoSave'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { Style } from 'inlines'
import { useUserContext } from '@app/util/hooks/useUserContext'
import { normalizePhoneInput } from '@app/util/helpers'

export const ContactDetailsStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: Partial<Initiative>
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app
  const creationTranslations = translations.dashboard.initiative.creation
  const { user } = useUserContext()

  const [contactPersonFirstName, setContactPersonFirstName] = useState(
    initiative.contactFirstName ?? '',
  )
  const [contactPersonLastName, setContactPersonLastName] = useState(
    initiative.contactLastName ?? '',
  )
  const [contactPersonEmail, setContactPersonEmail] = useState(
    initiative.contactEmail ?? '',
  )
  const [contactPersonPhone, setContactPersonPhone] = useState(() =>
    normalizePhoneInput(initiative.contactPhoneNumber ?? ''),
  )

  useInitiativeAutoSave(initiative.id, {
    contactFirstName: contactPersonFirstName,
    contactLastName: contactPersonLastName,
    contactEmail: contactPersonEmail,
    contactPhoneNumber: contactPersonPhone,
  })

  useEffect(() => {
    if (user) {
      if (!contactPersonFirstName) {
        setContactPersonFirstName(user.firstName)
      }
      if (!contactPersonLastName) {
        setContactPersonLastName(user.lastName)
      }
      if (!contactPersonEmail) {
        setContactPersonEmail(user.email)
      }
    }
  }, [user, contactPersonFirstName, contactPersonLastName, contactPersonEmail])

  useEffect(() => {
    if (contactPersonPhone.trim().length > 8) {
      completeStep(initiativeCreationSteps.contactDetails)
    } else {
      uncompleteStep(initiativeCreationSteps.contactDetails)
    }
  }, [
    contactPersonFirstName,
    contactPersonLastName,
    contactPersonEmail,
    contactPersonPhone,
    completeStep,
    uncompleteStep,
  ])

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={initiativeCreationSteps.contactDetails}
    >
      <Column gap="2.5rem" alignItems="stetch">
        <TextInput
          label={creationTranslations.contactPersonFirstName}
          labelStyle={labelStyle}
          text={contactPersonFirstName}
          setText={setContactPersonFirstName}
        />
        <TextInput
          label={creationTranslations.contactPersonLastName}
          labelStyle={labelStyle}
          text={contactPersonLastName}
          setText={setContactPersonLastName}
        />
        <TextInput
          label={creationTranslations.contactPersonEmailAdress}
          labelStyle={labelStyle}
          text={contactPersonEmail}
          setText={setContactPersonEmail}
        />
        <TextInput
          label={creationTranslations.contactPersonPhoneNumber}
          labelStyle={labelStyle}
          text={contactPersonPhone}
          setText={(value) => setContactPersonPhone(normalizePhoneInput(value))}
          inputType="tel"
          inputMode="tel"
          maxLength={16}
        />
      </Column>
    </CreationGuide>
  )
}

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}
